import { useAsclepius } from "@/composables/api/asclepius"
import { reactive, ref, Ref, watch } from "vue"
import {
  AsyncAppointment,
  AsyncAppointmentApiListAsyncAppointmentsRequest,
  CancelAsyncAppointmentResponse,
  ClaimAsyncAppointmentResponse,
  GetAsyncAppointmentResponse,
  ListAsyncAppointmentsResponse,
  MarkAsyncAppointmentChartedResponse,
  MarkAsyncAppointmentClosedResponse,
  MarkAsyncAppointmentOnHoldResponse
} from "@vitable/asclepius-api-client-ts"
// @ts-ignore
import store from "@/store"

export const useListAsyncAppointments = () => {
  const { getAsyncAppointmentApi, call, callResult } = useAsclepius()

  const asyncAppointments = reactive<Paginated<AsyncAppointment>>({
    currentPage: 1,
    nextPage: undefined,
    prevPage: undefined,
    totalCount: 0,
    results: []
  })

  async function listAsyncAppointments(request: AsyncAppointmentApiListAsyncAppointmentsRequest) {
    await call(() => getAsyncAppointmentApi().listAsyncAppointments(request))
  }

  watch(callResult, (newValue) => {
    if (newValue.isSuccess()) {
      const data: ListAsyncAppointmentsResponse = callResult.value.getData()
      asyncAppointments.results = data.results
      asyncAppointments.nextPage = data.next
      asyncAppointments.prevPage = data.previous
      asyncAppointments.totalCount = data.count
    }
  })

  return {
    listAsyncAppointments,
    listAsyncAppointmentsResult: callResult,
    asyncAppointments
  }
}

export const useGetAsyncAppointmentById = () => {
  const { getAsyncAppointmentApi, callWithResponse, callResult } = useAsclepius()
  const asyncAppointment: Ref<AsyncAppointment | undefined> = ref()

  async function getAsyncAppointmentById(asyncAppointmentId: string): Promise<Ref<AsyncAppointment | undefined>> {
    const response: GetAsyncAppointmentResponse = await callWithResponse(() =>
      getAsyncAppointmentApi().getAsyncAppointment({
        asyncAppointmentId
      })
    )
    asyncAppointment.value = response.async_appointment
    return asyncAppointment
  }

  watch(callResult, (newValue) => {
    if (newValue.isSuccess()) {
      const data: GetAsyncAppointmentResponse = callResult.value.getData()
      asyncAppointment.value = data.async_appointment
    }
  })

  return {
    asyncAppointment,
    getAsyncAppointmentById,
    getAsyncAppointmentByIdResult: callResult
  }
}

export const useCancelAsyncAppointment = () => {
  const { getAsyncAppointmentApi, callWithResponse, callResult } = useAsclepius()

  const cancelledAsyncAppointment: Ref<AsyncAppointment | undefined> = ref()

  async function cancelAsyncAppointment(asyncAppointmentId: string, cancelReason: string) {
    const asyncAppointmentResponse: CancelAsyncAppointmentResponse = await callWithResponse(() =>
      getAsyncAppointmentApi().cancelAsyncAppointment({
        asyncAppointmentId,
        cancelAsyncAppointmentRequest: { cancel_reason: cancelReason }
      })
    )
    cancelledAsyncAppointment.value = asyncAppointmentResponse.async_appointment
    return cancelledAsyncAppointment
  }

  return {
    cancelledAsyncAppointment,
    cancelAsyncAppointment,
    cancelAsyncAppointmentResult: callResult
  }
}

export function useUpdateAsyncAppointmentStatus(asyncAppointmentId: string) {
  const { getAsyncAppointmentApi, call, callResult } = useAsclepius()

  const isProvider: boolean = store.getters["me/isProvider"]
  const isClinicalAdmin: boolean = store.getters["me/isClinicalAdmin"]

  const asyncAppointment: Ref<AsyncAppointment | undefined> = ref<AsyncAppointment>()

  async function markAsyncAppointmentClaimed(): Promise<void> {
    if (!isProvider) {
      throw new Error("Must be a Provider to claim an Async Appointment!")
    }

    await call(() => getAsyncAppointmentApi().claimAsyncAppointment({ asyncAppointmentId }))

    const data: ClaimAsyncAppointmentResponse = callResult.value.data
    asyncAppointment.value = data.async_appointment
  }

  async function markAsyncAppointmentOnHold(): Promise<void> {
    if (!isProvider && !isClinicalAdmin) {
      throw new Error("Must be a Provider or Clinical Admin to place an Async Appointment on hold!")
    }

    await call(() => getAsyncAppointmentApi().markAsyncAppointmentOnHold({ asyncAppointmentId }))

    const data: MarkAsyncAppointmentOnHoldResponse = callResult.value.data
    asyncAppointment.value = data.async_appointment
  }

  async function markAsyncAppointmentCharted(): Promise<void> {
    if (!isProvider) {
      throw new Error("Must be a Provider to chart an Async Appointment!")
    }

    await call(() => getAsyncAppointmentApi().markAsyncAppointmentCharted({ asyncAppointmentId }))

    const data: MarkAsyncAppointmentChartedResponse = callResult.value.data
    asyncAppointment.value = data.async_appointment
  }

  async function markAsyncAppointmentClosed(): Promise<void> {
    if (!isProvider && !isClinicalAdmin) {
      throw new Error("Must be a Provider or Clinical Admin to close an Async Appointment!")
    }

    await call(() => getAsyncAppointmentApi().markAsyncAppointmentClosed({ asyncAppointmentId }))

    const data: MarkAsyncAppointmentClosedResponse = callResult.value.data
    asyncAppointment.value = data.async_appointment
  }

  return {
    asyncAppointment,
    markAsyncAppointmentClaimed,
    markAsyncAppointmentOnHold,
    markAsyncAppointmentCharted,
    markAsyncAppointmentClosed,
    updateAsyncAppointmentStatusResult: callResult
  }
}
